import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { Button } from './ui/button';
import { fmtInt } from '../lib/format';
import { t } from '../i18n';

interface Props<T> {
  rows: T[];
  columns: ColumnDef<T, unknown>[];
  initialSort?: SortingState;
  pageSize?: number;
  searchable?: boolean;
  caption?: React.ReactNode;
}

/** Sortable, paginated data table cleanly integrated into .panel without double-card effect. */
export function DataTable<T>({
  rows,
  columns,
  initialSort = [],
  pageSize = 15,
  searchable = true,
  caption,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
    globalFilterFn: 'includesString',
  });

  const { pageIndex } = table.getState().pagination;
  const total = table.getFilteredRowModel().rows.length;
  const first = total === 0 ? 0 : pageIndex * pageSize + 1;
  const last = Math.min(total, (pageIndex + 1) * pageSize);

  return (
    <div className="datatable space-y-4">
      {/* Table header bar: caption and search */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-1">
        {caption ? (
          <div className="caption text-sm font-medium text-dim">{caption}</div>
        ) : (
          <span />
        )}
        {searchable && (
          <div className="relative flex items-center min-w-[220px]">
            <Search className="absolute left-3 h-3.5 w-3.5 text-dim pointer-events-none" />
            <input
              type="search"
              className="h-8 w-full rounded-md border border-hair bg-well pl-8 pr-3 text-xs text-txt placeholder:text-dim transition-colors hover:border-hair2 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
              placeholder={t.search}
              aria-label={t.searchAria}
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setPageIndex(0);
              }}
            />
          </div>
        )}
      </div>

      {/* Table wrapper directly on panel surface without redundant inner card borders */}
      <div className="tablewrap overflow-x-auto w-full">
        <table className="table w-full">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sorted = h.column.getIsSorted();
                  const num = (h.column.columnDef.meta as { num?: boolean } | undefined)?.num;
                  return (
                    <th
                      key={h.id}
                      className={`${h.column.getCanSort() ? 'sortable cursor-pointer select-none' : ''} ${num ? 'num' : ''}`.trim()}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}
                      onClick={h.column.getToggleSortingHandler()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          h.column.toggleSorting();
                        }
                      }}
                      tabIndex={h.column.getCanSort() ? 0 : -1}
                    >
                      <div className={`inline-flex items-center gap-1.5 ${num ? 'justify-end' : 'justify-start'}`}>
                        <span>{flexRender(h.column.columnDef.header, h.getContext())}</span>
                        {h.column.getCanSort() && (
                          <span className="text-dim">
                            {sorted === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5 text-gold inline" />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className="h-3.5 w-3.5 text-gold inline" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-30 inline" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((r) => (
              <tr key={r.id}>
                {r.getVisibleCells().map((c) => {
                  const meta = c.column.columnDef.meta as { num?: boolean; className?: string } | undefined;
                  return (
                    <td
                      key={c.id}
                      className={`${meta?.num ? 'num' : ''} ${meta?.className ?? ''}`.trim()}
                    >
                      {flexRender(c.column.columnDef.cell, c.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {total === 0 && (
              <tr>
                <td colSpan={columns.length} className="empty">
                  {t.noRows}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between pt-3 text-xs text-mut border-t border-hair/40">
        <span className="font-medium text-dim">
          {t.rowsOf(first, last, fmtInt(total))}
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 px-2.5 flex items-center gap-1 text-xs"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            <span>{t.prev}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 px-2.5 flex items-center gap-1 text-xs"
          >
            <span>{t.next}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
